import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Divider, Tree } from 'antd';
import { CardLayout, cardLayoutOptions } from '../../Layout';
import { CardsList } from './CardsList';
import { CardsSearch } from './CardsSearch';
import { getCardSetHierarchyV2 } from '../../../middleware';
import { tF } from '../../../translate';
import { Catcher } from '../../Catcher';
import './styles.css';
import MainStyles from '../../styles/m_itemCardStyles.less';

const formatDate = (dateString: string | null) => (dateString ? new Date(dateString).toLocaleDateString() : '∞');

const CardSetTree: React.FC<{ data: any; card: any; level?: number }> = ({ data, card, level = 0 }) => {
    const companyLink = useCallback((companyId: number) => {
        return `/companies/${companyId}`;
    }, []);

    const cardSetLink = useCallback((cardSetId: number) => {
        return `/card_sets/${cardSetId}`;
    }, []);

    const getTreeData = (data: any, path = '') => {
        return [
            {
                title: (
                    <div style={{ display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
                        <span>
                            <strong>{tF('Card Set')}: </strong> <a href={cardSetLink(data.id)}>{data.name}</a>
                        </span>
                        <span>
                            <strong>{tF('Owner Company')}: </strong>
                            <a href={companyLink(data.company.id)}>{data.company.name}</a>
                        </span>
                        <span style={{ marginBottom: 8 }}>
                            <strong>{tF('Cards')}:</strong> <span>{data.assigned_cards.length}</span>
                        </span>
                        <CardsList assigned_cards={data.assigned_cards} card={card} />
                        <strong>{tF('Assigned Companies')}: </strong>
                    </div>
                ),
                key: `${path}card-set-${data.id}`,
                children: data.assigned_companies?.map((assignment: any, index: number) => ({
                    title: (
                        <div style={{ marginLeft: '5px' }} onClick={(e) => e.stopPropagation()}>
                            <div>
                                <a href={companyLink(assignment.company.id)}>{assignment.company.name}</a> |{' '}
                                <strong>Start:</strong> {formatDate(assignment.start_date)} | <strong>End:</strong>{' '}
                                {formatDate(assignment.end_date)} | <strong>Status:</strong>{' '}
                                {assignment.active ? 'Active' : 'Inactive'}
                            </div>
                        </div>
                    ),
                    key: `${path}assigned-company-${assignment.id}-${index}`,
                    children: assignment.company.card_sets?.map(
                        (cardSet: any, cardSetIndex: number) =>
                            getTreeData(cardSet, `${path}${index}-${cardSetIndex}-`)[0],
                    ),
                })),
            },
        ];
    };

    const treeData = getTreeData(data);

    return <Tree showLine={true} treeData={treeData} defaultExpandAll selectable={false} />;
};

export const CardSetHierarchy: React.FC = observer(() => {
    const [cardSetHierarchy, setCardSetHierarchy] = useState<any>(null);
    const [card, setCard] = useState<any>(null);

    useEffect(() => {
        (async () => {
            if (card) {
                const hierarchy = await getCardSetHierarchyV2(card.id);
                setCardSetHierarchy(hierarchy);
            }
        })();
    }, [card]);

    return (
        <Catcher>
            <div className={MainStyles.main}>
                <CardLayout
                    title={'Card Set Hierarchy'}
                    columnOption={cardLayoutOptions.cardSetHierarchyColumnOption}
                    idSpinners={['GET_CARD_SET_HIERARCHY', 'GET_CARD', 'GET_CARD_LIST']}
                >
                    <div>
                        <CardsSearch card={card} setSelectedCard={setCard} setHierarchy={setCardSetHierarchy} />
                        <Divider />
                        {cardSetHierarchy && <CardSetTree data={cardSetHierarchy} card={card} />}
                    </div>
                </CardLayout>
            </div>
        </Catcher>
    );
});
